import { useState, useEffect, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { 
  Mail, LogOut, CheckCircle, XCircle, Loader2, BookOpen, Palette,
  Clock, Star, ChevronRight, ChevronLeft, Copy, Download, ExternalLink,
  Sparkles, Brain, GraduationCap, Target, Zap, History, Heart, Trash2, X,
  Users, Plus, Search, Shield, ToggleLeft, ToggleRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HUMANIA_LOGO = "https://customer-assets.emergentagent.com/job_email-signin-flow/artifacts/7csvw41y_IMG_9570%20%281%29.png";
const BOOK_MOCKUP = "https://customer-assets.emergentagent.com/job_email-signin-flow/artifacts/cppec7xg_MOCKUP%20SISTEMA%20CALMA%20DIG.png";

// Admin email constant
const ADMIN_EMAIL = "earana2@xtec.cat";

// Glass card style for interior panels (translucent elegant)
const CARD_GLASS = "bg-white/[0.06] border border-purple-400/[0.18] shadow-[0_10px_30px_rgba(0,0,0,0.30)] backdrop-blur-sm";
const CARD_GLASS_ORANGE = "bg-white/[0.06] border border-orange-400/[0.18] shadow-[0_10px_30px_rgba(0,0,0,0.30)] backdrop-blur-sm";

// Light card style for home page cards
const CARD_LIGHT = "bg-[#F4ECFF] border-purple-200/50 shadow-lg";
const TEXT_DARK = "text-slate-800";
const TEXT_MUTED = "text-slate-600";

// Glass text colors
const TEXT_GLASS = "text-white";
const TEXT_GLASS_MUTED = "text-slate-300";

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    verifySession();
  }, []);

  const verifySession = async () => {
    const token = localStorage.getItem("session_token");
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/verify?token=${token}`);
      if (response.data.valid) {
        setIsAuthenticated(true);
        setEmail(response.data.email);
      } else {
        localStorage.removeItem("session_token");
      }
    } catch (e) {
      localStorage.removeItem("session_token");
    }
    setLoading(false);
  };

  const login = (token, userEmail) => {
    localStorage.setItem("session_token", token);
    setIsAuthenticated(true);
    setEmail(userEmail);
  };

  const logout = async () => {
    const token = localStorage.getItem("session_token");
    if (token) {
      try {
        await axios.post(`${API}/auth/logout?token=${token}`);
      } catch (e) {}
    }
    localStorage.removeItem("session_token");
    setIsAuthenticated(false);
    setEmail(null);
  };

  const getToken = () => localStorage.getItem("session_token");

  return (
    <AuthContext.Provider value={{ isAuthenticated, email, loading, login, logout, getToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900">
        <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Login Page (Keep existing)
const LoginPage = () => {
  const [email, setEmailInput] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const { login, isAuthenticated, loading } = useAuth();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setMessage("Validando…");

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: email.trim().toLowerCase()
      });

      if (response.data.success) {
        setStatus("success");
        setMessage("Acceso concedido");
        login(response.data.token, response.data.email);
        setTimeout(() => navigate("/", { replace: true }), 800);
      } else {
        setStatus("error");
        setMessage(response.data.message || "No autorizado");
        setTimeout(() => navigate("/unauthorized", { replace: true }), 1200);
      }
    } catch (err) {
      setStatus("error");
      setMessage("Error de conexión");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900">
        <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 p-4">
      <Card className="w-full max-w-md bg-slate-900/80 backdrop-blur border-purple-500/20 shadow-2xl">
        <CardHeader className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <img src={HUMANIA_LOGO} alt="HUMANIA" className="h-16 object-contain" style={{ filter: 'invert(1) hue-rotate(180deg) brightness(1.2)' }} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Tutor IA en Casa</h1>
          <p className="text-slate-400 text-sm leading-relaxed px-4">
            Menos peleas con los deberes.<br />
            Más aprendizaje y conexión en familia.
          </p>
          <div className="pt-4">
            <CardTitle className="text-lg font-semibold text-slate-300">Iniciar Sesión</CardTitle>
            <CardDescription className="text-slate-500 text-sm">
              Inicia sesión para activar tu tutor IA.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              data-testid="email-input"
              type="email"
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmailInput(e.target.value)}
              disabled={status === "loading" || status === "success"}
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-purple-500 h-12"
              required
            />
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={status === "loading" || status === "success" || !email.trim()}
              className="w-full h-12 bg-purple-600 hover:bg-purple-500 text-white font-medium"
            >
              {status === "loading" && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
              {status === "success" && <CheckCircle className="w-5 h-5 mr-2" />}
              {status === "error" && <XCircle className="w-5 h-5 mr-2" />}
              {status === "idle" ? "Acceder" : message}
            </Button>
          </form>
          {status === "error" && (
            <div data-testid="error-message" className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-red-400 text-sm text-center">{message}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Unauthorized Page
const UnauthorizedPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 p-4">
      <Card className="w-full max-w-md bg-slate-900/80 backdrop-blur border-red-500/20 shadow-2xl">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <CardTitle data-testid="unauthorized-title" className="text-2xl font-bold text-white">
            No Autorizado
          </CardTitle>
          <CardDescription className="text-slate-400">
            Tu email no está registrado en el sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            data-testid="back-to-login-btn"
            onClick={() => navigate("/login")}
            className="w-full h-12 bg-slate-700 hover:bg-slate-600 text-white"
          >
            Volver al Login
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Toast notification component
const Toast = ({ message, visible, onClose }) => {
  useEffect(() => {
    if (visible) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4">
      <div className="bg-purple-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
        <CheckCircle className="w-5 h-5" />
        <span>{message}</span>
      </div>
    </div>
  );
};

// ESTUDIO Flow Component
const EstudioFlow = ({ onClose, onComplete }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    edad: "",
    materia: "",
    otraMateria: "",
    tema: "",
    objetivos: [],
    perfiles: [],
    estilo: ""
  });

  const materias = [
    "Matemáticas", "Lengua", "Inglés", "Ciencias Naturales", "Física", 
    "Química", "Biología", "Historia", "Geografía", "Filosofía",
    "Educación Física", "Música", "Arte", "Tecnología", "Informática",
    "Otra materia"
  ];

  const objetivos = [
    { id: "entender", label: "Entender", icon: "💡" },
    { id: "resumir", label: "Resumir", icon: "📝" },
    { id: "esquema", label: "Crear esquema", icon: "🗺️" },
    { id: "examen", label: "Preparar examen", icon: "📚" },
    { id: "practicar", label: "Practicar", icon: "✏️" },
    { id: "repasar", label: "Repasar dudas", icon: "🔍" }
  ];

  const perfiles = [
    { id: "estandar", label: "Perfil estándar", desc: "" },
    { id: "tdah", label: "TDAH", desc: "pasos cortos y motivadores" },
    { id: "dislexia", label: "Dislexia", desc: "frases claras y visuales" },
    { id: "tea", label: "TEA / CEA", desc: "estructura y predictibilidad" },
    { id: "altas", label: "Altas capacidades", desc: "retos estimulantes" }
  ];

  const estilos = [
    { id: "simple", label: "Muy simple", icon: "🎯" },
    { id: "motivador", label: "Motivador", icon: "🌟" },
    { id: "pasos", label: "Paso a paso", icon: "📋" },
    { id: "examen", label: "Tipo examen", icon: "📝" }
  ];

  const togglePerfil = (id) => {
    setFormData(prev => ({
      ...prev,
      perfiles: prev.perfiles.includes(id)
        ? prev.perfiles.filter(p => p !== id)
        : [...prev.perfiles, id]
    }));
  };

  const toggleObjetivo = (id) => {
    setFormData(prev => ({
      ...prev,
      objetivos: prev.objetivos.includes(id)
        ? prev.objetivos.filter(o => o !== id)
        : [...prev.objetivos, id]
    }));
  };

  const canProceed = () => {
    switch (step) {
      case 1: return !!formData.edad;
      case 2: return !!formData.materia && (formData.materia !== "Otra materia" || formData.otraMateria);
      case 3: return !!formData.tema;
      case 4: return formData.objetivos.length > 0;
      case 5: return formData.perfiles.length > 0;
      case 6: return !!formData.estilo;
      default: return true;
    }
  };

  const generatePrompt = () => {
    const materiaFinal = formData.materia === "Otra materia" ? formData.otraMateria : formData.materia;
    const perfilesText = formData.perfiles.map(p => {
      const perfil = perfiles.find(pr => pr.id === p);
      return perfil ? `${perfil.label}${perfil.desc ? ` (${perfil.desc})` : ""}` : p;
    }).join(", ");
    const estiloObj = estilos.find(e => e.id === formData.estilo);
    const objetivosText = formData.objetivos.map(o => {
      const obj = objetivos.find(ob => ob.id === o);
      return obj ? obj.label : o;
    }).join(", ");

    return `# ROL DEL ASISTENTE
Eres un tutor IA especializado en educación personalizada. Tu misión es guiar el aprendizaje de manera efectiva y adaptada.

# CONTEXTO DEL ESTUDIANTE
- Edad: ${formData.edad}
- Materia: ${materiaFinal}
- Tema específico: ${formData.tema}
- Objetivos de la sesión: ${objetivosText}
- Perfiles de aprendizaje: ${perfilesText}
- Estilo de enseñanza preferido: ${estiloObj?.label || formData.estilo}

# INSTRUCCIONES PEDAGÓGICAS
1. Adapta tu lenguaje y explicaciones a la edad del estudiante (${formData.edad}).
2. Trabaja los siguientes objetivos: ${objetivosText} para la materia ${materiaFinal}.
3. Aborda el tema "${formData.tema}" de manera progresiva.

# ADAPTACIONES ESPECÍFICAS
${formData.perfiles.includes("tdah") ? "- TDAH: Usa pasos cortos (máximo 3 instrucciones a la vez), incluye pausas para verificar comprensión, celebra pequeños logros, mantén un tono motivador y dinámico.\n" : ""}${formData.perfiles.includes("dislexia") ? "- DISLEXIA: Utiliza frases cortas y claras, incluye elementos visuales y ejemplos concretos, evita bloques de texto largos, usa listas y viñetas.\n" : ""}${formData.perfiles.includes("tea") ? "- TEA/CEA: Proporciona estructura clara y predecible, anticipa los pasos a seguir, evita ambigüedades, usa instrucciones explícitas y literales.\n" : ""}${formData.perfiles.includes("altas") ? "- ALTAS CAPACIDADES: Incluye retos adicionales, conexiones interdisciplinarias, preguntas que fomenten el pensamiento crítico y la profundización.\n" : ""}${formData.perfiles.includes("estandar") ? "- PERFIL ESTÁNDAR: Equilibra explicaciones teóricas con ejemplos prácticos, verifica comprensión regularmente.\n" : ""}
# FORMATO DE RESPUESTA
- Estilo: ${estiloObj?.label || formData.estilo}
${formData.estilo === "simple" ? "- Usa el mínimo de palabras posible, explicaciones directas y sin rodeos." : ""}${formData.estilo === "motivador" ? "- Incluye palabras de ánimo, celebra el progreso, mantén un tono positivo y entusiasta." : ""}${formData.estilo === "pasos" ? "- Estructura todo en pasos numerados claros, un concepto por paso, verifica antes de avanzar." : ""}${formData.estilo === "examen" ? "- Presenta la información como si prepararas para un examen: puntos clave, posibles preguntas, errores comunes a evitar." : ""}

# INICIO
Comienza presentándote brevemente y preguntando qué sabe ya el estudiante sobre "${formData.tema}" para adaptar tu explicación.`;
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold ${TEXT_GLASS} text-center mb-6`}>¿Qué edad tiene el estudiante?</h3>
            <div className="grid grid-cols-3 gap-3">
              {["7-9 años", "10-12 años", "13-15 años"].map(edad => (
                <button
                  key={edad}
                  onClick={() => setFormData({ ...formData, edad })}
                  className={`p-4 rounded-xl border transition-all ${
                    formData.edad === edad
                      ? "border-purple-400 bg-purple-500/30 text-white"
                      : "border-white/20 bg-white/10 text-slate-200 hover:border-purple-400/50 hover:bg-white/15"
                  }`}
                >
                  <GraduationCap className="w-8 h-8 mx-auto mb-2 text-purple-300" />
                  <span className="font-medium">{edad}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold ${TEXT_GLASS} text-center mb-6`}>¿Qué materia?</h3>
            <select
              value={formData.materia}
              onChange={(e) => setFormData({ ...formData, materia: e.target.value })}
              className="w-full p-4 rounded-xl bg-white/10 border border-white/20 text-white focus:border-purple-400 outline-none"
            >
              <option value="" className="bg-slate-800">Selecciona una materia</option>
              {materias.map(m => (
                <option key={m} value={m} className="bg-slate-800">{m}</option>
              ))}
            </select>
            {formData.materia === "Otra materia" && (
              <Input
                placeholder="Escribe la materia..."
                value={formData.otraMateria}
                onChange={(e) => setFormData({ ...formData, otraMateria: e.target.value })}
                className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 h-12"
              />
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold ${TEXT_GLASS} text-center mb-6`}>¿Qué tema y curso?</h3>
            <Textarea
              placeholder="Ejemplo: 2º ESO — ecuaciones de primer grado"
              value={formData.tema}
              onChange={(e) => setFormData({ ...formData, tema: e.target.value })}
              className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 min-h-[100px]"
            />
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold ${TEXT_GLASS} text-center mb-2`}>¿Cuál es el objetivo?</h3>
            <p className={`${TEXT_GLASS_MUTED} text-center text-sm mb-4`}>(Puedes seleccionar varios)</p>
            <div className="grid grid-cols-2 gap-3">
              {objetivos.map(obj => (
                <button
                  key={obj.id}
                  onClick={() => toggleObjetivo(obj.id)}
                  className={`p-4 rounded-xl border transition-all text-left flex items-center justify-between ${
                    formData.objetivos.includes(obj.id)
                      ? "border-purple-400 bg-purple-500/30 text-white"
                      : "border-white/20 bg-white/10 text-slate-200 hover:border-purple-400/50 hover:bg-white/15"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{obj.icon}</span>
                    <p className="font-medium">{obj.label}</p>
                  </div>
                  {formData.objetivos.includes(obj.id) && (
                    <CheckCircle className="w-5 h-5 text-purple-300 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold ${TEXT_GLASS} text-center mb-2`}>Perfil específico</h3>
            <p className={`${TEXT_GLASS_MUTED} text-center text-sm mb-4`}>(Puedes seleccionar varios)</p>
            <div className="space-y-3">
              {perfiles.map(perfil => (
                <button
                  key={perfil.id}
                  onClick={() => togglePerfil(perfil.id)}
                  className={`w-full p-4 rounded-xl border transition-all text-left flex items-center justify-between ${
                    formData.perfiles.includes(perfil.id)
                      ? "border-purple-400 bg-purple-500/30 text-white"
                      : "border-white/20 bg-white/10 text-slate-200 hover:border-purple-400/50 hover:bg-white/15"
                  }`}
                >
                  <div>
                    <p className="font-medium">{perfil.label}</p>
                    {perfil.desc && <p className="text-sm text-slate-400">{perfil.desc}</p>}
                  </div>
                  {formData.perfiles.includes(perfil.id) && (
                    <CheckCircle className="w-5 h-5 text-purple-300" />
                  )}
                </button>
              ))}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold ${TEXT_GLASS} text-center mb-6`}>Estilo del tutor</h3>
            <div className="grid grid-cols-2 gap-3">
              {estilos.map(est => (
                <button
                  key={est.id}
                  onClick={() => setFormData({ ...formData, estilo: est.id })}
                  className={`p-4 rounded-xl border transition-all ${
                    formData.estilo === est.id
                      ? "border-purple-400 bg-purple-500/30 text-white"
                      : "border-white/20 bg-white/10 text-slate-200 hover:border-purple-400/50 hover:bg-white/15"
                  }`}
                >
                  <span className="text-2xl">{est.icon}</span>
                  <p className="font-medium mt-2">{est.label}</p>
                </button>
              ))}
            </div>
          </div>
        );

      case 7:
        const materiaFinal = formData.materia === "Otra materia" ? formData.otraMateria : formData.materia;
        return (
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold ${TEXT_GLASS} text-center mb-6`}>Resumen de tu selección</h3>
            <div className="bg-white/10 rounded-xl p-4 space-y-3 border border-white/20">
              <div className="flex justify-between">
                <span className={TEXT_GLASS_MUTED}>Edad:</span>
                <span className={`${TEXT_GLASS} font-medium`}>{formData.edad}</span>
              </div>
              <div className="flex justify-between">
                <span className={TEXT_GLASS_MUTED}>Materia:</span>
                <span className={`${TEXT_GLASS} font-medium`}>{materiaFinal}</span>
              </div>
              <div className="flex justify-between">
                <span className={TEXT_GLASS_MUTED}>Tema:</span>
                <span className={`${TEXT_GLASS} font-medium text-right max-w-[200px]`}>{formData.tema}</span>
              </div>
              <div className="flex justify-between">
                <span className={TEXT_GLASS_MUTED}>Objetivos:</span>
                <span className={`${TEXT_GLASS} font-medium text-right max-w-[200px]`}>
                  {formData.objetivos.map(o => objetivos.find(ob => ob.id === o)?.label).join(", ")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={TEXT_GLASS_MUTED}>Perfiles:</span>
                <span className={`${TEXT_GLASS} font-medium text-right max-w-[200px]`}>
                  {formData.perfiles.map(p => perfiles.find(pr => pr.id === p)?.label).join(", ")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={TEXT_GLASS_MUTED}>Estilo:</span>
                <span className={`${TEXT_GLASS} font-medium`}>{estilos.find(e => e.id === formData.estilo)?.label}</span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`${CARD_GLASS} rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto`}>
        <div className={`sticky top-0 ${CARD_GLASS} p-4 border-b border-purple-400/20 flex items-center justify-between rounded-t-2xl`}>
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-400" />
            <span className={`font-semibold ${TEXT_GLASS}`}>Estudio</span>
          </div>
          <div className="flex items-center gap-4">
            <span className={`${TEXT_GLASS_MUTED} text-sm`}>Paso {step} de 7</span>
            <button onClick={onClose} className={`${TEXT_GLASS_MUTED} hover:text-white`}>
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {renderStep()}
        </div>

        <div className={`sticky bottom-0 ${CARD_GLASS} p-4 border-t border-purple-400/20 flex justify-between rounded-b-2xl`}>
          <Button
            variant="outline"
            onClick={() => setStep(s => s - 1)}
            disabled={step === 1}
            className="border-white/20 text-slate-300 hover:bg-white/10"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
          </Button>
          
          {step < 7 ? (
            <Button
              onClick={() => setStep(s => s + 1)}
              disabled={!canProceed()}
              className="bg-purple-600 hover:bg-purple-500 text-white"
            >
              Siguiente <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={() => onComplete(generatePrompt(), formData, "estudio")}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Generar Prompt
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// CREADOR Flow Component
const CreadorFlow = ({ onClose, onComplete }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    interes: "",
    otroInteres: "",
    tiempo: "",
    actividad: "",
    edad: ""
  });

  const intereses = [
    { id: "dinosaurios", label: "Dinosaurios", icon: "🦖" },
    { id: "futbol", label: "Fútbol", icon: "⚽" },
    { id: "minecraft", label: "Minecraft", icon: "🎮" },
    { id: "superheroes", label: "Superhéroes", icon: "🦸" },
    { id: "espacio", label: "Espacio", icon: "🚀" },
    { id: "unicornios", label: "Unicornios", icon: "🦄" },
    { id: "arte", label: "Arte", icon: "🎨" },
    { id: "cocina", label: "Cocina", icon: "🍳" },
    { id: "otro", label: "Otro interés", icon: "✨" }
  ];

  const tiempos = [
    { id: "10-15", label: "10-15 min", icon: "⚡" },
    { id: "30", label: "30 min", icon: "⏱️" },
    { id: "60+", label: "1 hora +", icon: "🕐" }
  ];

  const actividades = [
    { id: "aventura", label: "Aventura interactiva", icon: "🗺️" },
    { id: "proyecto", label: "Proyecto creativo", icon: "🎨" },
    { id: "reto", label: "Reto de ingenio", icon: "🧩" },
    { id: "experimento", label: "Experimento", icon: "🧪" },
    { id: "manualidad", label: "Manualidad", icon: "✂️" },
    { id: "sorpresa", label: "Sorpréndeme", icon: "🎲" }
  ];

  const canProceed = () => {
    switch (step) {
      case 1: return !!formData.interes && (formData.interes !== "otro" || formData.otroInteres);
      case 2: return !!formData.tiempo;
      case 3: return !!formData.actividad;
      case 4: return !!formData.edad;
      default: return true;
    }
  };

  const generatePrompt = () => {
    const interesFinal = formData.interes === "otro" ? formData.otroInteres : intereses.find(i => i.id === formData.interes)?.label;
    const tiempoObj = tiempos.find(t => t.id === formData.tiempo);
    const actividadObj = actividades.find(a => a.id === formData.actividad);

    return `# ROL DEL ASISTENTE
Eres un creador de experiencias familiares interactivas y educativas. Tu misión es diseñar actividades que conecten a padres e hijos a través del juego y la creatividad.

# CONTEXTO DE LA FAMILIA
- Interés del niño/a hoy: ${interesFinal}
- Tiempo disponible: ${tiempoObj?.label || formData.tiempo}
- Tipo de actividad buscada: ${actividadObj?.label || formData.actividad}
- Edad del niño/a: ${formData.edad}

# INSTRUCCIONES
Crea DOS actividades diferentes basadas en el interés "${interesFinal}" adaptadas para la edad de ${formData.edad} y con una duración aproximada de ${tiempoObj?.label || formData.tiempo}.

El tipo de actividad debe ser: ${actividadObj?.label || formData.actividad}

# FORMATO PARA CADA ACTIVIDAD

## ACTIVIDAD 1: [Nombre creativo]

### Descripción breve
[2-3 frases que expliquen de qué trata la actividad]

### Materiales necesarios
- [Lista de materiales simples, preferiblemente cosas que ya hay en casa]

### Pasos a seguir
1. [Paso claro y concreto]
2. [Siguiente paso]
3. [Etc.]

### Variantes
- [Versión más sencilla para niños más pequeños]
- [Versión más retadora]
- [Variante sin materiales específicos]

---

## ACTIVIDAD 2: [Nombre creativo diferente]
[Mismo formato que la Actividad 1]

# CONSIDERACIONES
- Las instrucciones deben ser claras para que un adulto las pueda guiar fácilmente
- Los materiales deben ser accesibles (cosas de casa)
- Incluye momentos de interacción padre-hijo
- Añade preguntas o reflexiones para hacer juntos
- El tono debe ser divertido y motivador`;
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold ${TEXT_GLASS} text-center mb-6`}>¿Qué le apasiona hoy?</h3>
            <div className="grid grid-cols-3 gap-3">
              {intereses.map(int => (
                <button
                  key={int.id}
                  onClick={() => setFormData({ ...formData, interes: int.id })}
                  className={`p-3 rounded-xl border transition-all ${
                    formData.interes === int.id
                      ? "border-orange-400 bg-orange-500/30 text-white"
                      : "border-white/20 bg-white/10 text-slate-200 hover:border-orange-400/50 hover:bg-white/15"
                  }`}
                >
                  <span className="text-2xl block mb-1">{int.icon}</span>
                  <span className="text-sm font-medium">{int.label}</span>
                </button>
              ))}
            </div>
            {formData.interes === "otro" && (
              <Input
                placeholder="Escribe el interés..."
                value={formData.otroInteres}
                onChange={(e) => setFormData({ ...formData, otroInteres: e.target.value })}
                className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 h-12 mt-4"
              />
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold ${TEXT_GLASS} text-center mb-6`}>¿Cuánto tiempo tenéis?</h3>
            <div className="grid grid-cols-3 gap-3">
              {tiempos.map(t => (
                <button
                  key={t.id}
                  onClick={() => setFormData({ ...formData, tiempo: t.id })}
                  className={`p-4 rounded-xl border transition-all ${
                    formData.tiempo === t.id
                      ? "border-orange-400 bg-orange-500/30 text-white"
                      : "border-white/20 bg-white/10 text-slate-200 hover:border-orange-400/50 hover:bg-white/15"
                  }`}
                >
                  <span className="text-2xl block mb-2">{t.icon}</span>
                  <span className="font-medium">{t.label}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold ${TEXT_GLASS} text-center mb-6`}>¿Qué tipo de actividad buscáis?</h3>
            <div className="grid grid-cols-2 gap-3">
              {actividades.map(act => (
                <button
                  key={act.id}
                  onClick={() => setFormData({ ...formData, actividad: act.id })}
                  className={`p-4 rounded-xl border transition-all text-left ${
                    formData.actividad === act.id
                      ? "border-orange-400 bg-orange-500/30 text-white"
                      : "border-white/20 bg-white/10 text-slate-200 hover:border-orange-400/50 hover:bg-white/15"
                  }`}
                >
                  <span className="text-2xl">{act.icon}</span>
                  <p className="font-medium mt-2">{act.label}</p>
                </button>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold ${TEXT_GLASS} text-center mb-6`}>¿Qué edad tiene?</h3>
            <div className="grid grid-cols-3 gap-3">
              {["7-9 años", "10-12 años", "13-15 años"].map(edad => (
                <button
                  key={edad}
                  onClick={() => setFormData({ ...formData, edad })}
                  className={`p-4 rounded-xl border transition-all ${
                    formData.edad === edad
                      ? "border-orange-400 bg-orange-500/30 text-white"
                      : "border-white/20 bg-white/10 text-slate-200 hover:border-orange-400/50 hover:bg-white/15"
                  }`}
                >
                  <GraduationCap className="w-8 h-8 mx-auto mb-2 text-orange-300" />
                  <span className="font-medium">{edad}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 5:
        const interesFinal = formData.interes === "otro" ? formData.otroInteres : intereses.find(i => i.id === formData.interes)?.label;
        return (
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold ${TEXT_GLASS} text-center mb-6`}>Resumen de tu selección</h3>
            <div className="bg-white/10 rounded-xl p-4 space-y-3 border border-white/20">
              <div className="flex justify-between">
                <span className={TEXT_GLASS_MUTED}>Interés:</span>
                <span className={`${TEXT_GLASS} font-medium`}>{interesFinal}</span>
              </div>
              <div className="flex justify-between">
                <span className={TEXT_GLASS_MUTED}>Tiempo:</span>
                <span className={`${TEXT_GLASS} font-medium`}>{tiempos.find(t => t.id === formData.tiempo)?.label}</span>
              </div>
              <div className="flex justify-between">
                <span className={TEXT_GLASS_MUTED}>Actividad:</span>
                <span className={`${TEXT_GLASS} font-medium`}>{actividades.find(a => a.id === formData.actividad)?.label}</span>
              </div>
              <div className="flex justify-between">
                <span className={TEXT_GLASS_MUTED}>Edad:</span>
                <span className={`${TEXT_GLASS} font-medium`}>{formData.edad}</span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`${CARD_GLASS_ORANGE} rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto`}>
        <div className={`sticky top-0 ${CARD_GLASS_ORANGE} p-4 border-b border-orange-400/20 flex items-center justify-between rounded-t-2xl`}>
          <div className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-orange-400" />
            <span className={`font-semibold ${TEXT_GLASS}`}>Creador</span>
          </div>
          <div className="flex items-center gap-4">
            <span className={`${TEXT_GLASS_MUTED} text-sm`}>Paso {step} de 5</span>
            <button onClick={onClose} className={`${TEXT_GLASS_MUTED} hover:text-white`}>
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {renderStep()}
        </div>

        <div className={`sticky bottom-0 ${CARD_GLASS_ORANGE} p-4 border-t border-orange-400/20 flex justify-between rounded-b-2xl`}>
          <Button
            variant="outline"
            onClick={() => setStep(s => s - 1)}
            disabled={step === 1}
            className="border-white/20 text-slate-300 hover:bg-white/10"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
          </Button>
          
          {step < 5 ? (
            <Button
              onClick={() => setStep(s => s + 1)}
              disabled={!canProceed()}
              className="bg-orange-600 hover:bg-orange-500 text-white"
            >
              Siguiente <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={() => onComplete(generatePrompt(), formData, "creador")}
              className="bg-gradient-to-r from-orange-600 to-pink-600 hover:from-orange-500 hover:to-pink-500 text-white"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Generar Prompt
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Prompt Result Component
const PromptResult = ({ prompt, type, onClose, onSave }) => {
  const [toast, setToast] = useState({ visible: false, message: "" });
  const [saving, setSaving] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [showCheck, setShowCheck] = useState(false);

  useEffect(() => {
    // Show check animation after a brief moment
    const checkTimer = setTimeout(() => setShowCheck(true), 100);
    // Then show content
    const contentTimer = setTimeout(() => setShowContent(true), 600);
    return () => {
      clearTimeout(checkTimer);
      clearTimeout(contentTimer);
    };
  }, []);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setToast({ visible: true, message: "Prompt copiado al portapapeles" });
    } catch (err) {
      console.error("Error copying:", err);
    }
  };

  const downloadPrompt = () => {
    const blob = new Blob([prompt], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `prompt-${type}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setToast({ visible: true, message: "Prompt descargado" });
  };

  const openWithAI = async (aiUrl, aiName) => {
    try {
      await navigator.clipboard.writeText(prompt);
      setToast({ visible: true, message: `Prompt copiado: pégalo (Ctrl+V) en ${aiName}` });
      setTimeout(() => {
        window.open(aiUrl, "_blank");
      }, 500);
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave();
    setSaving(false);
    setToast({ visible: true, message: "Prompt guardado en historial" });
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Toast message={toast.message} visible={toast.visible} onClose={() => setToast({ ...toast, visible: false })} />
      
      <div className={`${CARD_GLASS} rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
        <div className={`sticky top-0 ${CARD_GLASS} p-4 border-b border-purple-400/20 flex items-center justify-between rounded-t-2xl`}>
          <div className="flex items-center gap-2">
            {showCheck && (
              <CheckCircle 
                className="w-5 h-5 text-emerald-400 animate-in zoom-in-50 duration-300" 
              />
            )}
            <span className={`font-semibold ${TEXT_GLASS}`}>Tu tutor IA está listo.</span>
          </div>
          <button onClick={onClose} className={`${TEXT_GLASS_MUTED} hover:text-white`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className={`p-6 transition-opacity duration-300 ${showContent ? 'opacity-100' : 'opacity-0'}`}>
          <div className="bg-white/10 rounded-xl p-4 border border-white/20 mb-6">
            <pre className={`whitespace-pre-wrap ${TEXT_GLASS_MUTED} text-sm font-mono overflow-x-auto max-h-[300px] overflow-y-auto`}>
              {prompt}
            </pre>
          </div>

          {/* Section 1: Get the prompt */}
          <div className="space-y-3 mb-6">
            <p className={`${TEXT_GLASS_MUTED} text-xs uppercase tracking-wider font-medium`}>Obtener prompt</p>
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={copyToClipboard}
                className="bg-purple-600 hover:bg-purple-500 text-white hover:scale-[1.02] transition-all duration-200"
                data-testid="copy-prompt-btn"
              >
                <Copy className="w-4 h-4 mr-2" /> Copiar prompt
              </Button>
              <Button
                onClick={downloadPrompt}
                className="bg-purple-600 hover:bg-purple-500 text-white hover:scale-[1.02] transition-all duration-200"
                data-testid="download-prompt-btn"
              >
                <Download className="w-4 h-4 mr-2" /> Descargar .txt
              </Button>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 my-6"></div>

          {/* Section 2: Use in AI */}
          <div className="space-y-3">
            <p className={`${TEXT_GLASS_MUTED} text-xs uppercase tracking-wider font-medium`}>Usar en</p>
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => openWithAI("https://chat.openai.com/", "ChatGPT")}
                className="bg-emerald-600 hover:bg-emerald-500 text-white hover:scale-[1.02] hover:shadow-lg hover:shadow-emerald-500/20 transition-all duration-200"
                data-testid="open-chatgpt-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" /> ChatGPT
              </Button>
              <Button
                onClick={() => openWithAI("https://gemini.google.com/", "Gemini")}
                className="bg-blue-600 hover:bg-blue-500 text-white hover:scale-[1.02] hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-200"
                data-testid="open-gemini-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" /> Gemini
              </Button>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 mt-6">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-purple-600 hover:bg-purple-500 text-white"
              data-testid="save-prompt-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-2" />}
              Guardar en historial
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// History/Favorites Modal
const HistoryModal = ({ isOpen, onClose, favoritesOnly }) => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ visible: false, message: "" });
  const { getToken } = useAuth();

  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(
        `${API}/prompts?token=${token}&favorites_only=${favoritesOnly}`
      );
      setPrompts(response.data);
    } catch (err) {
      console.error("Error fetching prompts:", err);
    }
    setLoading(false);
  }, [getToken, favoritesOnly]);

  useEffect(() => {
    if (isOpen) {
      fetchPrompts();
    }
  }, [isOpen, favoritesOnly, fetchPrompts]);

  const toggleFavorite = async (promptId) => {
    try {
      const token = getToken();
      await axios.post(`${API}/prompts/toggle-favorite?token=${token}`, { prompt_id: promptId });
      fetchPrompts();
    } catch (err) {
      console.error("Error toggling favorite:", err);
    }
  };

  const deletePrompt = async (promptId) => {
    try {
      const token = getToken();
      await axios.delete(`${API}/prompts/${promptId}?token=${token}`);
      fetchPrompts();
      setToast({ visible: true, message: "Prompt eliminado" });
    } catch (err) {
      console.error("Error deleting prompt:", err);
    }
  };

  const copyPrompt = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast({ visible: true, message: "Prompt copiado" });
    } catch (err) {
      console.error("Error copying:", err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Toast message={toast.message} visible={toast.visible} onClose={() => setToast({ ...toast, visible: false })} />
      
      <div className={`${CARD_GLASS} rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
        <div className={`sticky top-0 ${CARD_GLASS} p-4 border-b border-purple-400/20 flex items-center justify-between rounded-t-2xl`}>
          <div className="flex items-center gap-2">
            {favoritesOnly ? (
              <Heart className="w-5 h-5 text-pink-400" />
            ) : (
              <History className="w-5 h-5 text-purple-400" />
            )}
            <span className={`font-semibold ${TEXT_GLASS}`}>
              {favoritesOnly ? "Favoritos" : "Historial"}
            </span>
          </div>
          <button onClick={onClose} className={`${TEXT_GLASS_MUTED} hover:text-white`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
            </div>
          ) : prompts.length === 0 ? (
            <div className="text-center py-8">
              <p className={TEXT_GLASS_MUTED}>
                {favoritesOnly ? "No tienes favoritos aún" : "No hay prompts en el historial"}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {prompts.map((prompt) => (
                <div key={prompt.id} className="bg-white/10 rounded-xl p-4 border border-white/20">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {prompt.type === "estudio" ? (
                        <BookOpen className="w-4 h-4 text-blue-400" />
                      ) : (
                        <Palette className="w-4 h-4 text-orange-400" />
                      )}
                      <span className={`text-sm ${TEXT_GLASS_MUTED} capitalize`}>{prompt.type}</span>
                      <span className="text-xs text-slate-500">
                        {new Date(prompt.created_at).toLocaleDateString("es-ES")}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleFavorite(prompt.id)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          prompt.is_favorite ? "text-yellow-400" : "text-slate-500 hover:text-yellow-400"
                        }`}
                      >
                        <Star className="w-4 h-4" fill={prompt.is_favorite ? "currentColor" : "none"} />
                      </button>
                      <button
                        onClick={() => copyPrompt(prompt.prompt_text)}
                        className="p-1.5 rounded-lg text-slate-500 hover:text-white transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deletePrompt(prompt.id)}
                        className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <pre className={`${TEXT_GLASS_MUTED} text-xs font-mono whitespace-pre-wrap line-clamp-4 overflow-hidden`}>
                    {prompt.prompt_text.substring(0, 300)}...
                  </pre>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Admin Panel Component (only for earana2@xtec.cat)
const AdminPanel = ({ isOpen, onClose }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [toast, setToast] = useState({ visible: false, message: "" });
  const { getToken } = useAuth();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin/users?token=${token}&search=${searchTerm}`);
      setUsers(response.data);
    } catch (err) {
      console.error("Error fetching users:", err);
      setToast({ visible: true, message: "Error al cargar usuarios" });
    }
    setLoading(false);
  }, [getToken, searchTerm]);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen, fetchUsers]);

  const handleSearch = () => {
    fetchUsers();
  };

  const addUser = async () => {
    if (!newEmail.trim()) return;
    try {
      const token = getToken();
      await axios.post(`${API}/admin/users?token=${token}`, { email: newEmail.trim().toLowerCase() });
      setNewEmail("");
      fetchUsers();
      setToast({ visible: true, message: "Usuario añadido" });
    } catch (err) {
      setToast({ visible: true, message: err.response?.data?.detail || "Error al añadir usuario" });
    }
  };

  const toggleActive = async (userEmail, currentActive) => {
    try {
      const token = getToken();
      await axios.put(`${API}/admin/users/${userEmail}?token=${token}`, { active: !currentActive });
      fetchUsers();
      setToast({ visible: true, message: `Usuario ${!currentActive ? 'activado' : 'desactivado'}` });
    } catch (err) {
      setToast({ visible: true, message: "Error al actualizar usuario" });
    }
  };

  const deleteUser = async (userEmail) => {
    if (!window.confirm(`¿Eliminar ${userEmail}?`)) return;
    try {
      const token = getToken();
      await axios.delete(`${API}/admin/users/${userEmail}?token=${token}`);
      fetchUsers();
      setToast({ visible: true, message: "Usuario eliminado" });
    } catch (err) {
      setToast({ visible: true, message: err.response?.data?.detail || "Error al eliminar usuario" });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Toast message={toast.message} visible={toast.visible} onClose={() => setToast({ ...toast, visible: false })} />
      
      <div className={`${CARD_LIGHT} rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
        <div className={`sticky top-0 ${CARD_LIGHT} p-4 border-b border-purple-200 flex items-center justify-between rounded-t-2xl`}>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-600" />
            <span className={`font-semibold ${TEXT_DARK}`}>Panel Admin - Gestión de Usuarios</span>
          </div>
          <button onClick={onClose} className={`${TEXT_MUTED} hover:text-slate-800`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Add new user */}
          <div className="flex gap-2">
            <Input
              placeholder="Añadir email..."
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="flex-1 bg-white border-purple-200 text-slate-800"
              onKeyPress={(e) => e.key === 'Enter' && addUser()}
            />
            <Button onClick={addUser} className="bg-purple-600 hover:bg-purple-500 text-white">
              <Plus className="w-4 h-4 mr-1" /> Añadir
            </Button>
          </div>

          {/* Search */}
          <div className="flex gap-2">
            <Input
              placeholder="Buscar email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 bg-white border-purple-200 text-slate-800"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button onClick={handleSearch} variant="outline" className="border-purple-300 text-slate-700">
              <Search className="w-4 h-4" />
            </Button>
          </div>

          {/* Users list */}
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
            </div>
          ) : (
            <div className="space-y-2">
              {users.map((user) => (
                <div key={user.email} className="bg-white rounded-xl p-3 border border-purple-200 flex items-center justify-between">
                  <div className="flex-1">
                    <p className={`${TEXT_DARK} font-medium`}>{user.email}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      {user.source && <span className="bg-purple-100 text-purple-600 px-2 py-0.5 rounded">{user.source}</span>}
                      {user.created_at && <span>{new Date(user.created_at).toLocaleDateString("es-ES")}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleActive(user.email, user.active)}
                      className={`p-2 rounded-lg transition-colors ${user.active ? 'text-emerald-500' : 'text-slate-400'}`}
                      title={user.active ? 'Desactivar' : 'Activar'}
                    >
                      {user.active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={() => deleteUser(user.email)}
                      className="p-2 rounded-lg text-slate-400 hover:text-red-500 transition-colors"
                      disabled={user.email === ADMIN_EMAIL}
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Copilot Loader Component
const CopilotLoader = ({ onComplete }) => {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setStage(1), 200),
      setTimeout(() => setStage(2), 600),
      setTimeout(() => setStage(3), 1000),
      setTimeout(() => onComplete(), 1400),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="text-center">
        <div className="flex items-center justify-center gap-6 mb-8">
          {/* Brain icon */}
          <div className={`transition-all duration-500 ${stage >= 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}>
            <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Brain className="w-8 h-8 text-purple-400" />
            </div>
          </div>
          
          {/* Connecting pulse */}
          <div className={`flex gap-1 transition-all duration-300 ${stage >= 2 ? 'opacity-100' : 'opacity-0'}`}>
            <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse"></div>
            <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" style={{ animationDelay: '0.15s' }}></div>
            <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" style={{ animationDelay: '0.3s' }}></div>
          </div>
          
          {/* Robot icon */}
          <div className={`transition-all duration-500 ${stage >= 2 ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}>
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <span className="text-3xl">🤖</span>
            </div>
          </div>
        </div>
        
        <div className={`transition-all duration-500 ${stage >= 1 ? 'opacity-100' : 'opacity-0'}`}>
          <p className="text-purple-400 font-medium mb-2">Activando modo copiloto…</p>
          <p className={`text-slate-400 text-sm transition-all duration-500 ${stage >= 3 ? 'opacity-100' : 'opacity-0'}`}>
            Preparando tu tutor IA…
          </p>
        </div>
      </div>
    </div>
  );
};

// Home Page
const HomePage = () => {
  const { email, logout, getToken } = useAuth();
  const navigate = useNavigate();
  const [showEstudio, setShowEstudio] = useState(false);
  const [showCreador, setShowCreador] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState("");
  const [currentType, setCurrentType] = useState("");
  const [currentData, setCurrentData] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);

  const isAdmin = email === ADMIN_EMAIL;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const handlePromptComplete = (prompt, data, type) => {
    setCurrentPrompt(prompt);
    setCurrentData(data);
    setCurrentType(type);
    setShowEstudio(false);
    setShowCreador(false);
    setShowLoader(true);
  };

  const handleLoaderComplete = () => {
    setShowLoader(false);
    setShowResult(true);
  };

  const savePrompt = async () => {
    try {
      const token = getToken();
      await axios.post(`${API}/prompts?token=${token}`, {
        type: currentType,
        data: currentData,
        prompt_text: currentPrompt
      });
    } catch (err) {
      console.error("Error saving prompt:", err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900">
      {/* Loader */}
      {showLoader && (
        <CopilotLoader onComplete={handleLoaderComplete} />
      )}
      
      {/* Flows */}
      {showEstudio && (
        <EstudioFlow
          onClose={() => setShowEstudio(false)}
          onComplete={handlePromptComplete}
        />
      )}
      {showCreador && (
        <CreadorFlow
          onClose={() => setShowCreador(false)}
          onComplete={handlePromptComplete}
        />
      )}
      {showResult && (
        <PromptResult
          prompt={currentPrompt}
          type={currentType}
          onClose={() => setShowResult(false)}
          onSave={savePrompt}
        />
      )}
      <HistoryModal
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        favoritesOnly={false}
      />
      <HistoryModal
        isOpen={showFavorites}
        onClose={() => setShowFavorites(false)}
        favoritesOnly={true}
      />
      {isAdmin && (
        <AdminPanel
          isOpen={showAdmin}
          onClose={() => setShowAdmin(false)}
        />
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-900/80 backdrop-blur border-b border-purple-500/10">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={HUMANIA_LOGO} alt="HUMANIA" className="h-10 object-contain" style={{ filter: 'invert(1) hue-rotate(180deg) brightness(1.2)' }} />
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button
                  onClick={() => setShowAdmin(true)}
                  variant="ghost"
                  size="sm"
                  className="text-purple-300 hover:text-white hover:bg-purple-800"
                  data-testid="admin-btn"
                >
                  <Users className="w-4 h-4 mr-2" /> Admin
                </Button>
              )}
              <Button
                onClick={() => setShowHistory(true)}
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-slate-800"
                data-testid="history-btn"
              >
                <History className="w-4 h-4 mr-2" /> Historial
              </Button>
              <Button
                onClick={() => setShowFavorites(true)}
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-slate-800"
                data-testid="favorites-btn"
              >
                <Star className="w-4 h-4 mr-2" /> Favoritos
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-slate-800"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* BLOQUE 1 - TARJETA EDUCATIVA SIMPLIFICADA (tip pequeño) */}
      <div className="max-w-[520px] mx-auto px-4 pt-6">
        <div className="bg-white/[0.06] border border-emerald-400/20 rounded-full px-5 py-2 flex items-center justify-center gap-2 text-sm">
          <span className="text-emerald-400 whitespace-nowrap">🟢 Zona verde</span>
          <span className="text-slate-500">·</span>
          <span className="text-slate-300 whitespace-nowrap">Usa la IA como copiloto. El cerebro sigue al mando.</span>
        </div>
      </div>

      {/* Title Section - Improved Hierarchy */}
      <div className="max-w-6xl mx-auto px-4 pt-8 pb-2">
        <div className="text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Tutor IA en Casa
          </h1>
          <div className="inline-flex items-center gap-2 bg-purple-500/15 px-4 py-1.5 rounded-full border border-purple-400/30 mb-6">
            <Zap className="w-4 h-4 text-purple-400" />
            <span className="text-purple-300 font-medium text-sm">Modo Copiloto</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 pb-12">
        {/* Promise Section - Improved Hierarchy */}
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-semibold text-white mb-6 mt-2 leading-tight">
            Activa tu tutor IA particular en solo 3 clics.
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Consigue que estudie y haga los deberes sin peleas ni bloqueos.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 bg-purple-500/10 px-4 py-2 rounded-full border border-purple-500/20">
              <Brain className="w-5 h-5 text-purple-400" />
              <span className="text-purple-300 font-medium">Adaptado a Neurodiversidad</span>
            </div>
            <div className="flex items-center gap-2 bg-emerald-500/10 px-4 py-2 rounded-full border border-emerald-500/20">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <span className="text-emerald-300 font-medium">Apto para cualquier familia</span>
            </div>
          </div>
        </div>

        {/* Action Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* ESTUDIO Card */}
          <Card className="bg-white/[0.06] border border-purple-400/[0.18] backdrop-blur-[10px] shadow-[0_10px_30px_rgba(0,0,0,0.30)] hover:shadow-xl hover:shadow-blue-500/15 hover:border-blue-400/30 transition-all duration-300 group cursor-pointer"
                onClick={() => setShowEstudio(true)}
                data-testid="estudio-card">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-2xl">📘</span>
                </div>
                <CardTitle className="text-2xl text-white">ESTUDIO</CardTitle>
              </div>
              <CardDescription className="text-slate-300 text-base">
                Aprende mejor, estudia más fácil y disfruta descubriendo cosas nuevas.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-4 h-4 text-blue-400" />
                  Entender conceptos difíciles
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-4 h-4 text-blue-400" />
                  Preparar exámenes sin estrés
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-4 h-4 text-blue-400" />
                  Hacer deberes más rápido
                </li>
              </ul>
              <Button className="w-full bg-blue-600 hover:bg-blue-500 text-white group-hover:scale-[1.02] group-hover:shadow-lg group-hover:shadow-blue-500/25 transition-all duration-300"
                      data-testid="comenzar-estudio-btn">
                <BookOpen className="w-4 h-4 mr-2" /> Comenzar Estudio
              </Button>
            </CardContent>
          </Card>

          {/* CREADOR Card */}
          <Card className="bg-white/[0.06] border border-purple-400/[0.18] backdrop-blur-[10px] shadow-[0_10px_30px_rgba(0,0,0,0.30)] hover:shadow-xl hover:shadow-orange-500/15 hover:border-orange-400/30 transition-all duration-300 group cursor-pointer"
                onClick={() => setShowCreador(true)}
                data-testid="creador-card">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-2xl">🎨</span>
                </div>
                <CardTitle className="text-2xl text-white">CREADOR</CardTitle>
              </div>
              <CardDescription className="text-slate-300 text-base">
                Aventuras, retos creativos y proyectos para conectar en familia.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-4 h-4 text-orange-400" />
                  Aventuras interactivas épicas
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-4 h-4 text-orange-400" />
                  Conexión y vínculo familiar real
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-4 h-4 text-orange-400" />
                  Diversión sin pantallas pasivas
                </li>
              </ul>
              <Button className="w-full bg-orange-600 hover:bg-orange-500 text-white group-hover:scale-[1.02] group-hover:shadow-lg group-hover:shadow-orange-500/25 transition-all duration-300"
                      data-testid="crear-aventura-btn">
                <Palette className="w-4 h-4 mr-2" /> Crear aventura
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* BLOQUE 2 - PROMOCIÓN LIBRO */}
        <div className="mt-12">
          <div className="bg-gradient-to-br from-slate-800/80 via-slate-900/90 to-slate-800/80 rounded-2xl p-6 md:p-8 border border-slate-700/50 shadow-2xl">
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
              {/* Book Mockup */}
              <div className="flex-shrink-0 w-full md:w-auto flex justify-center">
                <img 
                  src={BOOK_MOCKUP} 
                  alt="Criar a la Generación IA" 
                  className="h-48 md:h-64 object-contain drop-shadow-2xl"
                />
              </div>
              {/* Text Content */}
              <div className="flex-1 text-center md:text-left">
                <p className="text-purple-400 text-sm font-semibold tracking-wider mb-2">SISTEMA CALMA DIGITAL</p>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                  Crianza entre pantallas. ¿Zombis o creadores?
                </h3>
                <p className="text-slate-300 mb-4 text-sm md:text-base leading-relaxed">
                  Tutor IA es solo una pieza del sistema completo. En{" "}
                  <span className="text-emerald-400 font-bold">CRIAR A LA GENERACIÓN IA</span>{" "}
                  encontrarás todas las estrategias para gestionar pantallas, neurodiversidad y su futuro entre algoritmos sin dramas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* FOOTER DE CONFIANZA */}
      <footer className="border-t border-purple-500/10 mt-12 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm mb-2">
            <span className="text-slate-300">Soporte:</span>{" "}
            <a href="mailto:contacto@humaniaedu.com" className="text-purple-400 hover:text-purple-300 transition-colors">
              contacto@humaniaedu.com
            </a>
          </p>
          <p className="text-slate-500 text-xs">
            La IA puede equivocarse. Se recomienda supervisión adulta.
          </p>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
